import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS, ZONES } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("baselineVolume.daily"), value: "day" }],
  week: [{ text: t("baselineVolume.weekly"), value: "week" }],
  month: [{ text: t("baselineVolume.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
    units: "metric",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesVolume = _.get(snapshot, "data.exercisesVolumeGroupZones", {});
  const exercisesVolumeMean = _.get(snapshot, "data.exercisesVolumeMean", {});
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetrics = getSelectedMetrics(exercisesVolume, query.type);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);
  const selectedMetricsGroups = _.values(selectedMetrics);
  const volumeSeries = _.map(ZONES, (zone) => {
    return {
      name: zone,
      className: zone,
      data: _.map(
        keys,
        (key) =>
          locals.fromMetric(
            _.sumBy(selectedMetricsGroups, `${key}.${zone}`),
            units
          ) || 0
      ),
    };
  });
  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}`))
  );

  const baselineVolume = locals.fromMetric(
    _.get(exercisesVolumeMean, query.type),
    units
  );
  const baselineSeries = _.map(keys, () => baselineVolume);
  const maxValue = _.max(totalVolumes.concat(baselineVolume));

  return (
    <div className="body metrics">
      <h1 className="alt">{t("baselineVolume.title")}</h1>
      <p>
        {t("baselineVolume.description")}
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="baselineTimeSpan"
                options={[
                  { text: t("baselineVolume.sixMonths"), value: "6-months" },
                  { text: t("baselineVolume.threeMonths"), value: "3-months" },
                  { text: t("baselineVolume.oneMonth"), value: "1-month" },
                ]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("baselineVolume.kg"), value: "metric" },
                  { text: t("baselineVolume.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("baselineVolume.total"), value: "total" },
                  { text: t("baselineVolume.squat"), value: "squat" },
                  { text: t("baselineVolume.bench"), value: "bench" },
                  { text: t("baselineVolume.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetrics) && (
            <div>
              <div className="legend">
                {_.map(ZONES, (zone) => {
                  return (
                    <div key={zone} className={`bar series ${zone}`}>
                      {_.capitalize(zone)}
                    </div>
                  );
                })}
                <div className="line series-a">{t("baselineVolume.baselineVolume")}</div>
              </div>
              <div className="axis-labels">
                <div className="left">{t("baselineVolume.volume")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        onlyInteger: true,
                        scaleMinSpace: 30,
                        high: maxValue,
                        low: 0,
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: volumeSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 40}px`,
                      height: "380px",
                      stackBars: true,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        low: 0,
                        high: maxValue,
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, period);
                      const dayRange = getDayRangeString(
                        [date, date.clone().add(1, period).subtract(1, "day")],
                        query
                      );
                      const totalVolume = totalVolumes[i];
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              {t("baselineVolume.volume")}{" "}
                              <b>{locals.withWeight(totalVolume, units)}</b>
                            </div>
                            <div>
                              {t("baselineVolume.baselineVolume")}{" "}
                              <b>{locals.withWeight(baselineVolume, units)}</b>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  {_.size(keys) >= 2 && (
                    <Chart
                      type="Line"
                      data={{ series: [baselineSeries] }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        showPoint: false,
                        lineSmooth:
                          process.browser &&
                          require("chartist").Interpolation.simple(),
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          low: 0,
                          high: maxValue,
                          showGrid: false,
                          showLabel: false,
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
